<template>
  <v-dialog v-model="dialog" v-bind="dialogAttrs" @input="inputHandler(value)">
    <template #activator="activatorProps">
      <slot
        name="activator"
        v-bind="{ ...activatorProps, value, submit, reset }"
      >
        <Textfield
          readonly
          :value="textFieldValue"
          v-bind="Object.assign(activatorProps.attrs, $attrs)"
          v-on="activatorProps.on"
          @click:clear="submit(null)"
        />
      </slot>
    </template>
    <BaseSection :title="localDialogTitle" disable-caps>
      <slot
        v-bind="{ on: contentListeners, value: activeValue, submit, reset }"
      />
      <template #actions>
        <BaseDialogActions @cancel="reset()" @continue="submit()" />
      </template>
    </BaseSection>
  </v-dialog>
</template>

<script>
/**
 * Any input that needs to be hidden behind a dialog can be added to the default slot like so:
 *
 * <DialogInputWrapper v-model=".......">
 *   <template #default={ on, value }>
 *     <YourInput v-on="on" :value="value"></YourInput>
 *   </template>
 * </DialogInputWrapper>
 *
 * This binds the input to the activeValue of this component, which is only submitted to the parent component when 'submit' is called.
 * You can also call submit/reset programmatically from your component if needed, simply expose them like 'on' and 'value'.
 *
 * -------
 *
 * You can override the default text field as activator with a component of your choice (for example
 * a button) by using the activator slot. Same rules apply as with the input in the dialog,
 * but the activator slot is to be used VISUALLY ONLY. Do NOT try to v-model the activator slot.
 */
export default {
  name: 'DialogInputWrapper',
  props: {
    dialogAttrs: {
      type: Object,
      default: () => ({ maxWidth: 550 }),
    },
    value: {
      type: null, // can be used for any value
      default: null,
    },
    valueFormatter: {
      type: Function,
      default: v => v,
    },
    inputValue: {
      type: [String, Number],
      default: '',
    },
    dialogTitle: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      dialog: false,
      activeValue: null,
    };
  },
  computed: {
    contentListeners() {
      return {
        // add handler for pressing enter to submit value?
        input: this.inputHandler,
      };
    },
    textFieldValue() {
      return this.inputValue || this.valueFormatter(this.value);
    },
    localDialogTitle() {
      return (
        this.dialogTitle || this.$attrs.label || this.$attrs.placeholder || ''
      );
    },
  },
  methods: {
    // inspired by Vuetify implementation
    // https://github.com/vuetifyjs/vuetify/blob/stable/packages/vuetify/src/components/VMenu/mixins/menu-generators.js
    inputHandler(e) {
      this.activeValue = e;
    },
    submit() {
      this.$emit('input', this.activeValue);
      this.$nextTick(() => this.reset());
    },
    reset() {
      this.dialog = false;
      this.activeValue = null;
    },
  },
};
</script>

<style lang="scss" scoped></style>
